import { useEffect, useState } from "react";
import { Alert, Dialog, DialogContent, Grid, IconButton } from "@mui/material";

import { FaExclamationCircle, FaPlus, FaTrash } from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiAutocomplete from "components/AutoComplete";
import OverlayLoader from "components/OverlayLoader";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

const { black, error } = colors;
const { borderWidth } = borders;

const QUESTION_TYPES = [
  {
    value: "rate_scale",
    label: "Rating Scale (1-5)",
  },
  {
    value: "multiple_choice",
    label: "Multiple Choice",
  },
  {
    value: "text_input",
    label: "Text Input",
  },
];

const createTemporaryId = (prefix = "item") =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const getQuestionTypeOption = (value) => {
  if (!value) {
    return QUESTION_TYPES[0];
  }

  const normalizedValue = typeof value === "object" ? value.value : value;

  return QUESTION_TYPES.find((item) => item.value === normalizedValue) || QUESTION_TYPES[0];
};

const emptyQuestion = () => ({
  id: createTemporaryId("question"),
  questionId: null,
  question: "",
  type: QUESTION_TYPES[0],
  isActive: true,
  options: [],
  optionsInput: "",
});

const normalizeOptions = (options = []) =>
  options.map((option, optionIndex) => {
    if (typeof option === "string") {
      return {
        id: createTemporaryId(`option_${optionIndex}`),
        optionId: null,
        label: option,
        isActive: true,
        sortOrder: optionIndex,
      };
    }

    return {
      id:
        option?.optionId || option?.id || option?._id || createTemporaryId(`option_${optionIndex}`),

      optionId: option?.optionId || option?.id || option?._id || null,

      label: option?.label || "",

      isActive: option?.isActive ?? true,

      sortOrder: option?.sortOrder ?? optionIndex,
    };
  });

const getInitialFormState = (form) => {
  const questions =
    Array.isArray(form?.questions) && form.questions.length > 0
      ? form.questions.map((question, questionIndex) => {
          const normalizedOptions = Array.isArray(question?.options)
            ? normalizeOptions(question.options)
            : [];

          return {
            id:
              question?.questionId ||
              question?.id ||
              question?._id ||
              createTemporaryId(`question_${questionIndex}`),

            questionId: question?.questionId || question?.id || question?._id || null,

            question: question?.questionText || question?.question || "",

            type: getQuestionTypeOption(question?.questionType || question?.type),

            isActive: question?.isActive ?? true,

            sortOrder: question?.sortOrder ?? questionIndex,

            options: normalizedOptions,

            optionsInput: normalizedOptions.map((option) => option.label).join(", "),
          };
        })
      : [emptyQuestion()];

  return {
    formName: form?.name || form?.formName || "",
    description: form?.description || "",
    questions,
  };
};

function GuestSatisfactionFormDialog({
  open,
  onClose,
  form,
  onSave,
  loading = false,
  error: apiError = "",
}) {
  const isEditMode = Boolean(form?.formId || form?.id || form?._id);

  const [formState, setFormState] = useState(() => getInitialFormState(form));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormState(getInitialFormState(form));
    setErrors({});
  }, [open, form]);

  const handleChange = (field, value) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));

    setErrors((current) => ({
      ...current,
      [field]: null,
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    setFormState((current) => {
      const questions = [...current.questions];

      questions[index] = {
        ...questions[index],
        [field]: value,
      };

      if (field === "type" && value?.value !== "multiple_choice") {
        questions[index].options = [];
      }

      return {
        ...current,
        questions,
      };
    });

    setErrors((current) => ({
      ...current,
      [`question_${index}_${field}`]: null,
    }));
  };

  const handleAddQuestion = () => {
    setFormState((current) => ({
      ...current,
      questions: [...current.questions, emptyQuestion()],
    }));

    setErrors((current) => ({
      ...current,
      questions: null,
    }));
  };

  const handleRemoveQuestion = (index) => {
    setFormState((current) => ({
      ...current,
      questions: current.questions.filter((_, questionIndex) => questionIndex !== index),
    }));
  };

  const handleOptionsChange = (questionIndex, value) => {
    const rawParts = value.split(",");

    const completedLabels = rawParts.map((item) => item.trim()).filter(Boolean);

    setFormState((current) => {
      const questions = [...current.questions];
      const existingOptions = questions[questionIndex]?.options || [];

      const options = completedLabels.slice(0, 5).map((label, optionIndex) => {
        const existingOption = existingOptions[optionIndex];

        return {
          id: existingOption?.id || createTemporaryId(`option_${optionIndex}`),

          optionId: existingOption?.optionId || null,

          label,

          isActive: existingOption?.isActive ?? true,

          sortOrder: optionIndex,
        };
      });

      questions[questionIndex] = {
        ...questions[questionIndex],

        optionsInput: value,

        options,
      };

      return {
        ...current,
        questions,
      };
    });

    if (completedLabels.length > 5) {
      setErrors((current) => ({
        ...current,
        [`question_${questionIndex}_options`]: "A maximum of 5 options is allowed",
      }));

      return;
    }

    setErrors((current) => ({
      ...current,
      [`question_${questionIndex}_options`]: null,
    }));
  };

  const validate = () => {
    const newErrors = {};

    const trimmedName = formState.formName?.trim();

    const trimmedDescription = formState.description?.trim();

    if (!trimmedName) {
      newErrors.formName = "Form name is required";
    } else if (trimmedName.length < 3) {
      newErrors.formName = "Form name must be at least 3 characters";
    }

    if (!trimmedDescription) {
      newErrors.description = "Description is required";
    } else if (trimmedDescription.length < 4) {
      newErrors.description = "Description must be at least 4 characters";
    }

    if (!Array.isArray(formState.questions) || formState.questions.length === 0) {
      newErrors.questions = "At least one question is required";
    }

    if (formState.questions.length > 20) {
      newErrors.questions = "A maximum of 20 questions is allowed";
    }

    formState.questions.forEach((question, index) => {
      const questionText = question?.question?.trim();

      if (!questionText) {
        newErrors[`question_${index}_question`] = "Question is required";
      } else if (questionText.length < 4) {
        newErrors[`question_${index}_question`] = "Question must be at least 4 characters";
      }

      if (!question?.type?.value) {
        newErrors[`question_${index}_type`] = "Question type is required";
      }

      if (question?.type?.value === "multiple_choice") {
        const options = Array.isArray(question.options)
          ? question.options.map((option) => option?.label?.trim()).filter(Boolean)
          : [];

        if (options.length < 2) {
          newErrors[`question_${index}_options`] = "Add at least 2 options";
        }

        const normalizedOptions = options.map((option) => option.toLowerCase());

        if (new Set(normalizedOptions).size !== normalizedOptions.length) {
          newErrors[`question_${index}_options`] = "Options must be unique";
        }
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (loading || !validate()) {
      return;
    }

    const questions = formState.questions.map((question, questionIndex) => {
      const questionType = question.type?.value;

      const payloadQuestion = {
        id: question.id,

        questionId: question.questionId || undefined,

        question: question.question.trim(),

        questionText: question.question.trim(),

        type: questionType,

        questionType,

        isActive: question.isActive ?? true,

        sortOrder: questionIndex,
      };

      if (questionType === "multiple_choice") {
        payloadQuestion.options = (question.options || []).map((option, optionIndex) => ({
          id: option.id,

          optionId: option.optionId || undefined,

          label: option.label.trim(),

          isActive: option.isActive ?? true,

          sortOrder: optionIndex,
        }));
      }

      return payloadQuestion;
    });

    onSave({
      ...form,

      name: formState.formName.trim(),

      formName: formState.formName.trim(),

      description: formState.description.trim(),

      questions,
    });
  };

  const handleDialogClose = (event, reason) => {
    if (loading) {
      return;
    }

    onClose?.(event, reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          position: "relative",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent>
        <OverlayLoader
          open={loading}
          message={isEditMode ? "Updating satisfaction form..." : "Creating satisfaction form..."}
        />

        <VuiBox mb={2}>
          <VuiTypography variant="h5" color="dark" fontWeight="bold">
            {isEditMode ? "Edit Guest Satisfaction Form" : "Create Guest Satisfaction Form"}
          </VuiTypography>

          <VuiTypography
            display="block"
            variant="caption"
            color="dark"
            fontWeight="regular"
            sx={{
              mt: 0.3,
              opacity: 0.72,
            }}
          >
            Build a lightweight survey to collect guest feedback.
          </VuiTypography>
        </VuiBox>

        <VuiBox mb={3}>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <VuiTypography
                display="block"
                variant="caption"
                color="dark"
                fontWeight="medium"
                mb={0.5}
              >
                Form Name
              </VuiTypography>

              <VuiInput
                gradient
                fullWidth
                value={formState.formName}
                onChange={(event) => handleChange("formName", event.target.value)}
                placeholder="Guest Satisfaction Form"
                disabled={loading}
                inputProps={{ maxLength: 100 }}
              />

              {errors.formName && (
                <VuiTypography display="block" variant="caption" color="error" mt={0.4}>
                  {errors.formName}
                </VuiTypography>
              )}
            </Grid>

            <Grid item xs={12}>
              <VuiTypography
                display="block"
                variant="caption"
                color="dark"
                fontWeight="medium"
                mb={0.5}
              >
                Description
              </VuiTypography>

              <VuiInput
                gradient
                fullWidth
                value={formState.description}
                onChange={(event) => handleChange("description", event.target.value)}
                placeholder="Collect guest feedback after their stay"
                multiline
                rows={3}
                disabled={loading}
                inputProps={{ maxLength: 200 }}
              />

              {errors.description && (
                <VuiTypography display="block" variant="caption" color="error" mt={0.4}>
                  {errors.description}
                </VuiTypography>
              )}
            </Grid>
          </Grid>
        </VuiBox>

        <VuiBox mb={2}>
          <VuiBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            mb={1.5}
          >
            <VuiBox>
              <VuiTypography variant="button" color="dark" fontWeight="bold">
                Question Builder
              </VuiTypography>

              <VuiTypography display="block" variant="caption" color="dark" sx={{ opacity: 0.65 }}>
                {formState.questions.length}/20 questions
              </VuiTypography>
            </VuiBox>

            <VuiButton
              color="primary"
              size="small"
              onClick={handleAddQuestion}
              startIcon={<FaPlus />}
              disabled={loading || formState.questions.length >= 20}
            >
              Add Question
            </VuiButton>
          </VuiBox>

          {errors.questions && (
            <VuiTypography display="block" variant="caption" color="error" mb={1}>
              {errors.questions}
            </VuiTypography>
          )}

          {formState.questions.map((question, index) => (
            <VuiBox
              key={question.id || index}
              mb={1.5}
              p={2}
              borderRadius="16px"
              sx={questionCardStyles}
            >
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <VuiTypography variant="caption" color="dark" fontWeight="bold">
                  Question {index + 1}
                </VuiTypography>

                {formState.questions.length > 1 && (
                  <IconButton
                    onClick={() => handleRemoveQuestion(index)}
                    disabled={loading}
                    sx={{
                      color: error.main,

                      background: rgba(error.main, 0.08),

                      border: `${borderWidth[1]} solid ${rgba(error.main, 0.18)}`,

                      "&:hover": {
                        background: rgba(error.main, 0.14),
                      },
                    }}
                  >
                    <FaTrash size={13} />
                  </IconButton>
                )}
              </VuiBox>

              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <VuiTypography
                    display="block"
                    variant="caption"
                    color="dark"
                    fontWeight="medium"
                    mb={0.5}
                  >
                    Question
                  </VuiTypography>

                  <VuiInput
                    gradient
                    fullWidth
                    value={question.question}
                    onChange={(event) =>
                      handleQuestionChange(index, "question", event.target.value)
                    }
                    placeholder="How would you rate your stay?"
                    disabled={loading}
                    inputProps={{ maxLength: 250 }}
                  />

                  {errors[`question_${index}_question`] && (
                    <VuiTypography display="block" variant="caption" color="error" mt={0.4}>
                      {errors[`question_${index}_question`]}
                    </VuiTypography>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <VuiTypography
                    display="block"
                    variant="caption"
                    color="dark"
                    fontWeight="medium"
                    mb={0.5}
                  >
                    Question Type
                  </VuiTypography>

                  <VuiAutocomplete
                    fullWidth
                    options={QUESTION_TYPES}
                    value={question.type}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    placeholder="Select type"
                    gradient
                    disabled={loading}
                    onChange={(_, selected) =>
                      handleQuestionChange(index, "type", selected || QUESTION_TYPES[0])
                    }
                  />

                  {errors[`question_${index}_type`] && (
                    <VuiTypography display="block" variant="caption" color="error" mt={0.4}>
                      {errors[`question_${index}_type`]}
                    </VuiTypography>
                  )}
                </Grid>

                {question.type?.value === "multiple_choice" && (
                  <Grid item xs={12}>
                    <VuiTypography
                      display="block"
                      variant="caption"
                      color="dark"
                      fontWeight="medium"
                      mb={0.5}
                    >
                      Options
                    </VuiTypography>

                    <VuiInput
                      gradient
                      fullWidth
                      value={question.optionsInput || ""}
                      onChange={(event) => handleOptionsChange(index, event.target.value)}
                      placeholder="Excellent, Good, Average, Poor"
                      disabled={loading}
                      inputProps={{ maxLength: 10000 }}
                    />

                    <VuiTypography
                      display="block"
                      variant="caption"
                      color="dark"
                      fontWeight="regular"
                      mt={0.4}
                      sx={{ opacity: 0.65 }}
                    >
                      Separate options with commas.
                    </VuiTypography>

                    {errors[`question_${index}_options`] && (
                      <VuiTypography display="block" variant="caption" color="error" mt={0.4}>
                        {errors[`question_${index}_options`]}
                      </VuiTypography>
                    )}
                  </Grid>
                )}
              </Grid>
            </VuiBox>
          ))}
        </VuiBox>

        <VuiBox pb={1}>
          {apiError && (
            <VuiBox
              display="flex"
              alignItems="center"
              gap={1}
              p={1.2}
              mt={2}
              borderRadius="md"
              sx={{
                border: "1px solid rgba(239,68,68,0.4)",
                background: "rgba(239,68,68,0.08)",
              }}
            >
              <FaExclamationCircle size={14} color="#ef4444" />

              <VuiTypography variant="caption" color="error">
                {typeof apiError === "string"
                  ? apiError
                  : apiError?.message || "Something went wrong. Please try again."}
              </VuiTypography>
            </VuiBox>
          )}
        </VuiBox>

        <VuiBox display="flex" justifyContent="flex-end" gap={1.5} mt={2}>
          <VuiButton
            variant="outlined"
            color="primary"
            onClick={handleDialogClose}
            disabled={loading}
          >
            Cancel
          </VuiButton>

          <VuiButton color="primary" onClick={handleSubmit} disabled={loading}>
            {isEditMode ? "Update Form" : "Create Form"}
          </VuiButton>
        </VuiBox>
      </DialogContent>
    </Dialog>
  );
}

const questionCardStyles = {
  background: rgba(black.main, 0.025),

  border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
};

export default GuestSatisfactionFormDialog;
